import { Component, Inject, OnDestroy, OnInit, ViewEncapsulation } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, EMPTY, Observable, Subscription } from "rxjs";
import { map } from "rxjs/internal/operators/map";
import { catchError, finalize, tap } from "rxjs/operators";
import {
  ColumnSorterInfo,
  ConfigScope,
  ConfigSettingType,
  DefaultPage,
  FieldType,
  FilterEvent,
  FormatType,
  PageEvent,
  SearchEvent,
  SortEvent,
  TableConfig,
  TableEvent,
} from "src/app/models/custom-table/custom-table.model";
import { SnackbarType } from "src/app/models/snackbar/snackbar.model";
import { PlaylistsService } from "src/app/shared/services/playlists/playlists.service";
import { SnackbarService } from "src/app/shared/services/snackbar/snackbar.service";
import { TableConfigService } from "src/app/shared/services/table-config/table-config.service";

const USAGE_CODE = "SELECT_SEQUENCE";
@Component({
  selector: "app-select-sequence-dialog",
  templateUrl: "./select-sequence-dialog.component.html",
  styleUrls: ["./select-sequence-dialog.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class SelectSequenceDialogComponent implements OnInit, OnDestroy {
  isLoading$ = new BehaviorSubject<boolean>(false);
  private loadingCounter = 0;

  totalSequences$ = new BehaviorSubject<number>(0);
  public preselectedIds: string[] = []; 

  public sequencesList: any[];

  private sequencesSubscription: Subscription;

  public selectedRow: Object[] = [];
  public selectedSequences: string[]= [];

  public columnInfo$: Observable<ColumnSorterInfo[]>;
  tableConfig: TableConfig[] = [
    {
      header: "CustomGrid.Options",
      field: FieldType.OPTIONS,
      fieldType: FieldType.OPTIONS,
    },
    {
      header: "Sequence.name",
      field: "name",
      fieldType: FieldType.STRING,
      searchable: true,
    },
    {
      header: "Sequence.token",
      field: "token",
      fieldType: FieldType.STRING,
      searchable: true,
      hidden: true,
      identifier: true,
    },
    {
      header: "Sequence.multimediaFiles",
      field: "multimediaFiles",
      fieldType: FieldType.ARRAY,
    },
    {
      header: "Sequence.createdOn",
      field: "createdOn",
      fieldType: FieldType.DATE,
      formatType: FormatType.DATETIME,
      hidden: true,
    },
    {
      header: "Sequence.updatedOn",
      field: "updatedOn",
      fieldType: FieldType.DATE,
      formatType: FormatType.DATETIME,
      hidden: true,
    },
  ];

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: any,
    private playlistsService: PlaylistsService,
    private snackbarService: SnackbarService,
    private translate: TranslateService,
    private tableConfigService: TableConfigService,
    private dialogRef: MatDialogRef<SelectSequenceDialogComponent>
  ) {}

  ngOnInit(): void {
    this.columnInfo$ = this.getTableConfig();
    this.loadTableData();

    this.selectedSequences = this.data.preselectedSequences;
    this.preselectedIds = this.selectedSequences.map(token => token);
  }

  loadTableData(
    pageEvent?: PageEvent,
    sortEvent?: SortEvent,
    searchEvent?: SearchEvent,
    filterEvent?: FilterEvent
  ) {
    const { pageIndex, pageSize } = pageEvent || {};
    this.setLoading(true);
    this.sequencesSubscription = this.playlistsService
      .getSequencesList(
        {
          skip: (pageIndex * pageSize) | DefaultPage.PAGE_INDEX,
          top: pageSize ?? DefaultPage.PAGE_SIZE,
          realSize: true,
        },
        searchEvent,
        sortEvent,
        filterEvent
      )
      .pipe(
        map(({ result }) => {
          this.totalSequences$.next(result.size); 
          return result.items.map((sequence) => {
            const { multimediaFiles } = sequence;
            return {
              ...sequence,
              multimediaFiles: multimediaFiles?.map((m) => ({
                ...m,
                arrayDescription: m.name,
              })),
            };
          });
        }),
        catchError((error) => {
          this.snackbarService.show({
            message: this.translate.instant("Sequence.sequencesLoadError"),
            type: SnackbarType.ERROR,
          });
          return EMPTY;
        }),
        finalize(() => this.setLoading(false))
      )
      .subscribe((result: any[]) => (this.sequencesList = result));
  }

  getTableConfig() {
    this.setLoading(true);
    return this.tableConfigService
      .getConfigSettings(USAGE_CODE, ConfigSettingType.TABLE_CONFIG)
      .pipe(
        map(
          (response) =>
            <ColumnSorterInfo[]>response.result?.value?.tableColumnInfo
        ),
        catchError((error) => {
          this.snackbarService.show({
            message: this.translate.instant("TableConfig.LoadError"),
            type: SnackbarType.ERROR,
          });
          return EMPTY;
        }),
        finalize(() => this.setLoading(false))
      );
  }

  onSelectedRow(event) {
    this.selectedRow = event;
  }

  onSaveTableConfig(tableColumnInfo: ColumnSorterInfo[]) {
    this.setLoading(true);
    this.tableConfigService
      .saveConfigSettings({
        code: USAGE_CODE,
        settingTypeCode: ConfigSettingType.TABLE_CONFIG,
        scope: ConfigScope.USER,
        value: { tableColumnInfo },
      })
      .pipe(
        tap(() =>
          this.snackbarService.show({
            message: this.translate.instant("TableConfig.SaveSuccess"),
            type: SnackbarType.SUCCESS,
          })
        ),
        catchError((error) => {
          this.snackbarService.show({
            message: this.translate.instant("TableConfig.SaveError"),
            type: SnackbarType.SUCCESS,
          });
          return EMPTY;
        }),
        finalize(() => this.setLoading(false))
      )
      .subscribe();
  }

  onGetTableData(tableEvent: TableEvent) {
    const { pageEvent, sortEvent, searchEvent, filterEvent } = tableEvent;
    this.loadTableData(pageEvent, sortEvent, searchEvent, filterEvent);
  }

  onConfirmClick() {
    this.dialogRef.close(this.selectedRow);
  }

  isSelectedRowEmpty(): boolean {
    return this.selectedRow.length == 0;
  }

  private setLoading(loading: boolean) {
    this.loadingCounter += loading ? 1 : -1;
    this.isLoading$.next(this.loadingCounter > 0);
  }

  ngOnDestroy(): void {
    if (this.sequencesSubscription) {
      this.sequencesSubscription.unsubscribe();
    }
  }
}
